import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Leaf, Snowflake, Award, Truck, Star, ChevronRight, Droplets, Package, ThumbsUp, Thermometer, Zap, Wind, CheckCircle2 } from "lucide-react";
import { CATEGORIES, PRODUCTS, TESTIMONIALS } from "@/data";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const stagger = {
  visible: { transition: { staggerChildren: 0.12 } }
};

// ── Hero Section ──
const HeroSection = () => {
  const heroImages = [
    "https://images.unsplash.com/photo-1438907046657-4ae137eb8c5e?w=1200&q=80",
    "https://images.unsplash.com/photo-1587063666834-1ef04db9ce08?w=1200&q=80",
    "https://images.unsplash.com/photo-1498557850523-fd3d118b962e?w=1200&q=80",
    "https://images.pexels.com/photos/5870328/pexels-photo-5870328.jpeg?auto=compress&cs=tinysrgb&w=1200"
  ];
  const [currentSlide, setCurrentSlide] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [heroImages.length]);

  return (
    <section className="relative min-h-[90vh] flex items-center pt-20 overflow-hidden" data-testid="hero-section">
      <div className="absolute inset-0 bg-gradient-to-br from-[#f8f8f8] via-white to-green-50/30" />

      <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-12 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
          {/* Left: Text Content */}
          <motion.div initial="hidden" animate="visible" variants={stagger} className="py-8 lg:py-0">
            <motion.div variants={fadeUp}>
              <Badge className="bg-green-100 text-[#2B7A1E] hover:bg-green-100 mb-6 px-4 py-1.5 text-xs font-semibold tracking-wide rounded-full border-0">
                FSSAI Certified &middot; ISO 22000
              </Badge>
            </motion.div>

            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#333] leading-[1.1] tracking-tight mb-6" style={{ fontFamily: 'Montserrat' }}>
              Premium Frozen{" "}
              <span className="text-gradient-green">Fruits, Pulps</span>
              <br />& Slices
            </motion.h1>

            <motion.p variants={fadeUp} className="text-base md:text-lg text-gray-600 leading-relaxed max-w-lg mb-8">
              Sourced from trusted farms, processed to international quality standards. Delivering consistent, preservative-free frozen ingredients to food businesses across India.
            </motion.p>

            <motion.div variants={fadeUp} className="flex flex-wrap gap-4">
              <Link to="/products">
                <Button className="rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white font-semibold px-8 h-12 shadow-lg shadow-green-900/20 text-base" data-testid="hero-explore-btn">
                  Explore Products <ArrowRight size={18} className="ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="rounded-full border-2 border-[#2B7A1E] text-[#2B7A1E] font-semibold px-8 h-12 hover:bg-green-50 text-base" data-testid="hero-quote-btn">
                  Get a Quote
                </Button>
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="flex items-center gap-6 mt-10">
              {[
                { icon: <Leaf size={16} />, text: "100% Natural" },
                { icon: <Snowflake size={16} />, text: "IQF Technology" },
                { icon: <Truck size={16} />, text: "Pan India Delivery" }
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-2 text-sm text-gray-500">
                  <span className="text-[#2B7A1E]">{item.icon}</span>
                  <span>{item.text}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Image Showcase */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="relative hidden lg:block"
          >
            <div className="relative w-full aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
              {heroImages.map((img, idx) => (
                <div key={idx} className={`hero-slide ${idx === currentSlide ? 'active' : ''}`}>
                  <img src={img} alt={`Frozen food products ${idx + 1}`} className="w-full h-full object-cover" loading={idx === 0 ? "eager" : "lazy"} />
                </div>
              ))}
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-6 bg-white rounded-2xl p-4 shadow-xl animate-hero-float">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <ThumbsUp size={18} className="text-[#2B7A1E]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-[#333]" style={{ fontFamily: 'Montserrat' }}>200+</p>
                  <p className="text-xs text-gray-500">Happy Clients</p>
                </div>
              </div>
            </div>
            {/* Dots indicator */}
            <div className="absolute bottom-4 right-4 flex gap-2">
              {heroImages.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentSlide(idx)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${idx === currentSlide ? 'bg-white w-6' : 'bg-white/50'}`}
                  data-testid={`hero-dot-${idx}`}
                />
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// ── Trust Bar ──
const trustIconVariant = {
  hidden: { scale: 0.4, rotate: -20, opacity: 0 },
  visible: { scale: 1, rotate: 0, opacity: 1, transition: { type: "spring", stiffness: 280, damping: 14 } }
};

const TrustBar = () => (
  <section className="bg-white border-y border-gray-100 shadow-md" data-testid="trust-bar">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12 py-8">
      <motion.div
        className="flex flex-wrap justify-center gap-8 md:gap-14"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-60px" }}
        variants={stagger}
      >
        {[
          { icon: <ShieldCheck size={28} />, text: "FSSAI Licensed" },
          { icon: <Award size={28} />, text: "ISO 22000" },
          { icon: <Leaf size={28} />, text: "No Preservatives" },
          { icon: <Snowflake size={28} />, text: "Cold Chain Assured" },
          { icon: <Truck size={28} />, text: "Pan India Supply" }
        ].map((item) => (
          <motion.div key={item.text} variants={fadeUp} className="flex items-center gap-3 text-gray-500">
            <motion.span className="text-[#2B7A1E]" variants={trustIconVariant}>
              {item.icon}
            </motion.span>
            <span className="text-base font-semibold" style={{ fontFamily: 'Montserrat' }}>{item.text}</span>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

// ── Categories Section ──
const catCardVariant = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: "easeOut" } }
};

const catGridStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.18, delayChildren: 0.1 } }
};

const CategoriesSection = ({ categories }) => (
  <section className="py-16 md:py-24 bg-[#f8f8f8]" data-testid="categories-section">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}>
        <motion.div variants={fadeUp} className="mb-12">
          <span className="text-sm font-semibold text-[#2B7A1E] uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>Our Range</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#333] mt-2 tracking-tight" style={{ fontFamily: 'Montserrat' }}>
            Premium Product Categories
          </h2>
          <div className="w-16 h-1 bg-[#2B7A1E] mt-4 rounded-full" />
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={catGridStagger}
        >
          {categories.map((cat, idx) => (
            <motion.div key={cat.id} variants={catCardVariant}>
              <Link to={`/products?category=${cat.id}`} data-testid={`category-card-${cat.id}`}>
                <div className={`group relative overflow-hidden rounded-3xl ${idx === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}>
                  <div className="aspect-[3/2]">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover transition-transform duration-500 ease-out group-hover:scale-110"
                      loading="lazy"
                    />
                  </div>
                  <div className="category-overlay absolute inset-0 flex flex-col justify-end p-6">
                    <Badge className="bg-[#2B7A1E] text-white border-0 w-fit mb-2 text-xs">{cat.productCount} Products</Badge>
                    <h3 className="text-xl font-bold text-white mb-1" style={{ fontFamily: 'Montserrat' }}>{cat.name}</h3>
                    <p className="text-white/80 text-sm line-clamp-2">{cat.description}</p>
                    <span className="text-white/90 text-sm font-medium mt-2 flex items-center gap-1 group-hover:gap-2 transition-all">
                      Explore <ChevronRight size={14} />
                    </span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// ── Why Choose Us ──
const WhyChooseUs = () => {
  const features = [
    { icon: <Award size={34} />, title: "Premium Quality", desc: "Handpicked fruits processed using modern IQF and cold-chain technology for superior quality." },
    { icon: <Snowflake size={34} />, title: "Freshness Locked", desc: "Advanced blast-freezing preserves nutrition, natural flavour, and vibrant color." },
    { icon: <Package size={34} />, title: "Reliable Supply", desc: "Year-round availability with consistent quality in every batch, every delivery." },
    { icon: <Droplets size={34} />, title: "100% Natural", desc: "No preservatives, no artificial colors. Just pure, natural frozen goodness." },
    { icon: <Truck size={34} />, title: "Fast Delivery", desc: "Cold-chain enabled logistics across India ensuring safe, fresh transport." },
    { icon: <ShieldCheck size={34} />, title: "Certified Quality", desc: "FSSAI licensed, ISO 22000 compliant with rigorous quality checks at every stage." }
  ];

  return (
    <section className="py-16 md:py-24 bg-white" data-testid="why-choose-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
          <motion.div variants={fadeUp} className="text-center mb-14">
            <span className="text-sm font-semibold text-[#2B7A1E] uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>Why Choose Us</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#333] mt-2 tracking-tight" style={{ fontFamily: 'Montserrat' }}>
              What Makes Us Different
            </h2>
            <div className="w-16 h-1 bg-[#2B7A1E] mt-4 mx-auto rounded-full" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((f) => (
              <motion.div key={f.title} variants={fadeUp}>
                <Card className="group p-8 rounded-2xl bg-white border border-gray-100 hover:border-green-300 hover:-translate-y-2 transition-all duration-300 h-full shadow-sm hover:shadow-[0_16px_48px_rgba(43,122,30,0.18)]">
                  <div className="w-16 h-16 rounded-2xl bg-green-50 flex items-center justify-center text-[#2B7A1E] mb-5 transition-transform duration-300 group-hover:scale-125 group-hover:rotate-6">
                    {f.icon}
                  </div>
                  <h3 className="text-lg font-bold text-[#333] mb-2" style={{ fontFamily: 'Montserrat' }}>{f.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{f.desc}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ── Featured Products ──
const FeaturedProducts = ({ products }) => (
  <section className="py-16 md:py-24 bg-[#f8f8f8]" data-testid="featured-products-section">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
        <motion.div variants={fadeUp} className="flex items-end justify-between mb-12">
          <div>
            <span className="text-sm font-semibold text-[#2B7A1E] uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>Featured</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#333] mt-2 tracking-tight" style={{ fontFamily: 'Montserrat' }}>
              Our Best Sellers
            </h2>
            <div className="w-16 h-1 bg-[#2B7A1E] mt-4 rounded-full" />
          </div>
          <Link to="/products" className="hidden md:flex items-center gap-2 text-[#2B7A1E] font-semibold text-sm hover:gap-3 transition-all" data-testid="view-all-products-link">
            View All Products <ArrowRight size={16} />
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.slice(0, 8).map((product) => (
            <motion.div key={product.id} variants={fadeUp}>
              <Link to={`/products/${product.id}`} data-testid={`featured-product-${product.id}`}>
                <div className="group bg-white rounded-3xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <div className="aspect-square product-img-zoom">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="p-5">
                    <span className="text-xs text-[#2B7A1E] font-semibold uppercase tracking-wide">{product.categoryName}</span>
                    <h3 className="text-base font-bold text-[#333] mt-1 group-hover:text-[#2B7A1E] transition-colors" style={{ fontFamily: 'Montserrat' }}>
                      {product.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">{product.shortDescription}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-xs text-[#2B7A1E] font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        View Details <ChevronRight size={12} />
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="md:hidden mt-8 text-center">
          <Link to="/products">
            <Button className="rounded-full bg-[#2B7A1E] hover:bg-[#236618] text-white font-semibold px-8" data-testid="view-all-products-btn-mobile">
              View All Products <ArrowRight size={16} className="ml-2" />
            </Button>
          </Link>
        </div>
      </motion.div>
    </div>
  </section>
);

// ── Storage Facility Section ──
const storageFeatures = [
  { icon: Thermometer, label: "Cool Room",          value: "100 TON · -18°C to 22°C" },
  { icon: Snowflake,   label: "Blast Freezing",     value: "1 TON · -40°C · HACCP Controlled" },
  { icon: Wind,        label: "IQF Technology",     value: "Individual Quick Freezing" },
  { icon: Zap,         label: "Power Backup",       value: "Generator backup — uninterrupted cold chain" },
  { icon: Thermometer, label: "Digital Monitoring", value: "Temperature logs shared with clients" },
  { icon: CheckCircle2,label: "Client Benefits",    value: "No security deposit · Bulk discount policy" },
];

const StorageFacilitySection = () => (
  <section className="py-16 md:py-24 bg-[#f4f9f3]" data-testid="storage-facility-section">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
      {/* Heading */}
      <motion.div
        initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger}
        className="text-center mb-12"
      >
        <motion.span variants={fadeUp} className="text-sm font-semibold text-[#2B7A1E] uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>
          Infrastructure
        </motion.span>
        <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-[#333] mt-2 tracking-tight" style={{ fontFamily: 'Montserrat' }}>
          Our Storage Facility
        </motion.h2>
        <motion.div variants={fadeUp} className="w-16 h-1 bg-[#2B7A1E] mt-4 mx-auto rounded-full" />
        <motion.p variants={fadeUp} className="mt-4 text-gray-600 max-w-2xl mx-auto text-base">
          State-of-the-art cold storage in Pune with blast freezing, IQF technology and round-the-clock digital temperature monitoring.
        </motion.p>
      </motion.div>

      {/* Two-column layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        {/* Left — image */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={fadeUp}
          className="grid grid-cols-2 gap-3"
        >
          <img src="/cold2.webp" alt="Cold storage interior" className="col-span-2 w-full h-60 object-cover rounded-2xl shadow-md" />
          <img src="/cold3.webp" alt="Blast freezing unit" className="w-full h-36 object-cover rounded-2xl shadow-md" />
          <img src="/cold4.webp" alt="Temperature monitoring" className="w-full h-36 object-cover rounded-2xl shadow-md" />
        </motion.div>

        {/* Right — feature list */}
        <motion.div
          initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-60px" }} variants={stagger}
          className="flex flex-col gap-3"
        >
          {storageFeatures.map(({ icon: Icon, label, value }) => (
            <motion.div
              key={label}
              variants={fadeUp}
              className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-md hover:border-green-100 transition-all duration-300"
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#2B7A1E]/10 flex items-center justify-center">
                <Icon size={20} className="text-[#2B7A1E]" />
              </div>
              <div>
                <p className="text-sm font-semibold text-[#333]" style={{ fontFamily: 'Montserrat' }}>{label}</p>
                <p className="text-sm text-gray-500">{value}</p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </div>
  </section>
);

// ── Testimonials ──
const TestimonialsSection = ({ testimonials }) => (
  <section className="py-16 md:py-24 bg-white" data-testid="testimonials-section">
    <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-12">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-100px" }} variants={stagger}>
        <motion.div variants={fadeUp} className="text-center mb-12">
          <span className="text-sm font-semibold text-[#2B7A1E] uppercase tracking-wider" style={{ fontFamily: 'Montserrat' }}>Testimonials</span>
          <h2 className="text-3xl md:text-4xl font-bold text-[#333] mt-2 tracking-tight" style={{ fontFamily: 'Montserrat' }}>
            What Our Clients Say
          </h2>
          <div className="w-16 h-1 bg-[#2B7A1E] mt-4 mx-auto rounded-full" />
        </motion.div>

        <motion.div variants={fadeUp}>
          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {testimonials.map((t) => (
                <CarouselItem key={t.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <div className="p-6 rounded-2xl bg-[#f8f8f8] h-full flex flex-col" data-testid={`testimonial-${t.id}`}>
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                          key={i}
                          size={16}
                          className={i < t.rating ? "star-filled fill-yellow-400" : "star-empty"}
                        />
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 leading-relaxed flex-1 italic">"{t.text}"</p>
                    <div className="mt-4 pt-4 border-t border-gray-200">
                      <p className="text-sm font-bold text-[#333]" style={{ fontFamily: 'Montserrat' }}>{t.name}</p>
                      <p className="text-xs text-gray-500">{t.role}, {t.company}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4" data-testid="testimonial-prev" />
            <CarouselNext className="hidden md:flex -right-4" data-testid="testimonial-next" />
          </Carousel>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// ── CTA Section ──
const CTASection = () => (
  <section className="py-16 md:py-20 bg-[#2B7A1E] relative overflow-hidden grain-overlay" data-testid="cta-section">
    <img
      src="/cold1.webp"
      alt=""
      aria-hidden="true"
      className="absolute inset-0 w-full h-full object-cover opacity-30"
    />
    <div className="relative max-w-4xl mx-auto px-4 md:px-8 text-center z-10">
      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
        <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-white tracking-tight mb-4" style={{ fontFamily: 'Montserrat' }}>
          Ready to Source Premium Frozen Ingredients?
        </motion.h2>
        <motion.p variants={fadeUp} className="text-white/80 text-base md:text-lg mb-8 max-w-2xl mx-auto">
          Partner with Shri Ram Frozen Foods for consistent quality, competitive pricing, and reliable year-round supply.
        </motion.p>
        <motion.div variants={fadeUp} className="flex flex-wrap gap-4 justify-center">
          <Link to="/contact">
            <Button className="rounded-full bg-white text-[#2B7A1E] font-bold px-8 h-12 hover:bg-gray-100 text-base shadow-lg" data-testid="cta-contact-btn">
              Contact Us Today
            </Button>
          </Link>
          <a href="https://wa.me/918308203097?text=Hi%2C%20I%20need%20details%20about%20frozen%20food%20products." target="_blank" rel="noopener noreferrer">
            <Button variant="outline" className="rounded-full border-2 border-white text-white font-semibold px-8 h-12 hover:bg-white/10 text-base" data-testid="cta-whatsapp-btn">
              WhatsApp Us
            </Button>
          </a>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

// ── Main Home Component ──
export default function Home() {
  const categories = CATEGORIES;
  const featuredProducts = PRODUCTS.filter(p => p.featured);
  const testimonials = TESTIMONIALS;

  return (
    <div data-testid="home-page">
      <HeroSection />
      <TrustBar />
      <CategoriesSection categories={categories} />
      <WhyChooseUs />
      <FeaturedProducts products={featuredProducts} />
      <StorageFacilitySection />
      <TestimonialsSection testimonials={testimonials} />
      <CTASection />
    </div>
  );
}
